import React, { useEffect } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Namespace } from 'i18next';
import { TryAgainLink } from 'LoggedOut/LoggedOutScreenWrapper/LoggedOutScreenWrapper.styled';
import { useHistory, useLocation } from 'react-router';
import RoutePath from 'App/RoutePath';
import { EmailAddress } from '../EmailConfirmation/EmailConfirmation.styled';
import {
  LoggedOutScreenWrapperFooter,
  LoggedOutScreenSubtitle,
  LoggedOutScreenTitle,
  LoggedOutScreenWrapper,
} from '../LoggedOutScreenWrapper';
import {
  EmailConfirmationFooter,
  EmailConfirmationHelperText,
} from '../EmailConfirmation/EmailConfirmationShared';

export interface SignUpEmailConfirmationProps {
  email?: string;
  redirectTo?: string;
}

function SignUpEmailConfirmation({
  email,
  redirectTo,
}: SignUpEmailConfirmationProps) {
  const { t: translate } =
    useTranslation<Namespace<'logged_out'>>('logged_out');
  const history = useHistory();
  const { state } = useLocation<{ email?: string; redirectTo?: string }>();
  const userEmail = email || state?.email;
  const redirect = state?.redirectTo || redirectTo;

  useEffect(() => {
    if (!userEmail) {
      history.push(RoutePath.Login);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userEmail]);

  const onRedirect = () => {
    history.push({
      pathname: redirect || RoutePath.Home,
      state: {
        ...state,
        email: userEmail,
      },
    });
  };

  return (
    <LoggedOutScreenWrapper dataTestId="v2-email-confirmation-screen">
      <LoggedOutScreenTitle>
        {translate('sign_up.sign_up_email_confirmation.heading')}
      </LoggedOutScreenTitle>
      <LoggedOutScreenSubtitle data-testid="email-confirmation-subtitle">
        {Trans({
          t: translate,
          i18nKey: 'sign_up.sign_up_email_confirmation.subtitle',
          values: { email: userEmail },
          defaults:
            'We’ve sent a verification email to <email_address>{{email}}</email_address>',
          components: { email_address: <EmailAddress /> },
        })}
      </LoggedOutScreenSubtitle>
      {redirect && (
        <EmailConfirmationHelperText>
          {Trans({
            t: translate,
            i18nKey: 'sign_up.sign_up_email_confirmation.helper_text',
            values: { email: userEmail },
            defaults:
              'Wrong email? <confirmation_link>Try again</confirmation_link>',
            components: {
              confirmation_link: (
                <TryAgainLink role="link" onClick={onRedirect}>
                  Try again
                </TryAgainLink>
              ),
            },
          })}
        </EmailConfirmationHelperText>
      )}
      <LoggedOutScreenWrapperFooter>
        <EmailConfirmationFooter userEmailAddress={email} />
      </LoggedOutScreenWrapperFooter>
    </LoggedOutScreenWrapper>
  );
}

export default SignUpEmailConfirmation;
